import { action, observable } from 'mobx';
import { api } from '../config';
import { toJS } from 'mobx';

// Helpers
import { formatTime, getPath } from '../helpers/helpers';

// Dependencies
import moment from 'moment';

// Components
import SessionStore from './SessionStore';
import ScheduleStore from './ScheduleStore';

let obx = observable({
  defaultQuery: '?limit=25',
  reportFilters: {},
  reportFilterMap: {},
  reportData: null,
  reportTotal: null,
  reportFormat: null,
  reportNext: null,
  // Export data
  reportMethod: 'GET',
  reportUrl: null,
  reportDefinition: null,
  reportExportTitle: null,
  // Create load more url
  getNextUrl: action(function(url) {
    let nextUrl = getPath(url);
    return nextUrl.replace('/v1', '');
  }),
  // Append data to report
  appendReportData: action(function(data) {
    obx.reportData = obx.reportData.concat(data);
  }),
  // Apply filters to query
  applyFilters: action(function() {
    let query = obx.defaultQuery;

    if(Object.keys(obx.reportFilters).length > 0) {
      for(let key in obx.reportFilters) {
        if(obx.reportFilters[key]) {
          if(!Array.isArray(toJS(obx.reportFilters[key]))) {
            query += `&${key}=${obx.reportFilters[key]}`
          } else {
            for(let i = 0; i < obx.reportFilters[key].length; i++) {
              query+= `&${key}=${obx.reportFilters[key][i]}`
            }
          }
        }
      }
    }

    return query;
  }),
  // Update a query parameter
  updateQueryParam(url, key, value) {
    const re = new RegExp(`([?&])${key}=.*?(&|$)`, 'i');
    const separator = url.indexOf('?') !== -1 ? '&' : '?';

    if(url.match(re)) {
      return url.replace(re, `$1${key}=${value}$2`);
    } else {
      return `${url}${separator}${key}=${value}`;
    }
  },
  // Generic function to get report data
  getReportData: action(function(report) {
    switch (report) {
      case 'exams':
        obx.getExamReport();
        break;

      case 'sessions':
        obx.getSessionReport();
        break;

      default:
        return null;
    }
  }),
  // Exam report
  formatExamReport: action(function(data) {
    const formattedData = [];

    data.forEach(record => {
      let permittedItems = [];

      if(record.configuration.permittedBathroomBreak) {
        permittedItems.push('Bathroom Break');
      }

      if(record.configuration.permittedBlankPaper) {
        permittedItems.push('Blank Paper');
      }

      if(record.configuration.permittedCalculator) {
        permittedItems.push('Calculator');
      }

      if(record.configuration.permittedDictionary) {
        permittedItems.push('Dictionary');
      }

      if(record.configuration.permittedEarplugs) {
        permittedItems.push('Earplugs');
      }

      if(record.configuration.permittedFormulaSheet) {
        permittedItems.push('Formula Sheet');
      }

      if(record.configuration.permittedNotes) {
        permittedItems.push('Notes');
      }

      if(record.configuration.permittedTextbook) {
        permittedItems.push('Textbook');
      }

      if(record.configuration.permittedWebsites) {
        permittedItems.push('Websites');
      }

      if(record.configuration.permittedOtherItems) {
        permittedItems.push(record.configuration.permittedOtherItems);
      }

      const newRow = {
        account: record.accountName,
        exam: {
          examTitle: record.examTitle,
          duration: `Duration: ${formatTime(record.configuration.duration)}`
        },
        course: record.courseTitle,
        examDate: {
          examOpenDate: moment(record.configuration.examOpenDate),
          examCloseDate: moment(record.configuration.examCloseDate)
        },
        password: record.configuration.password,
        permittedItems: permittedItems,
      }

      formattedData.push(newRow);
    });

    // Store CSV format
    obx.reportDefinition = {
      "Account": "{{accountName | customEscape}}",
      "Exam": "{{examTitle | customEscape}}",
      "Duration": "{{configuration.duration | customEscape}} minutes",
      "Course": "{{courseTitle | customEscape}}",
      "Open Date": "{{configuration.examOpenDate | time_zone: '"+moment.tz.guess()+"', 'M/D/YYYY [at] h:mma z'}}",
      "Closes": "{{configuration.examCloseDate | time_zone: '"+moment.tz.guess()+"', 'M/D/YYYY [at] h:mma z'}}",
      "Password": "{{configuration.password | customEscape}}",
      "Bathroom Break Permitted": "{% if configuration.permittedBathroomBreak %}Yes{% endif %}",
      "Blank Paper Permitted": "{% if configuration.permittedBlankPaper %}Yes{% endif %}",
      "Calculator Permitted": "{% if configuration.permittedCalculator %}Yes{% endif %}",
      "Dictionary Permitted": "{% if configuration.permittedDictionary %}Yes{% endif %}",
      "Earplugs Permitted": "{% if configuration.permittedEarplugs %}Yes{% endif %}",
      "Formula Sheets Permitted": "{% if configuration.permittedFormulaSheet %}Yes{% endif %}",
      "Notes Permitted": "{% if configuration.permittedNotes %}Yes{% endif %}",
      "Textbook Permitted": "{% if configuration.permittedTextbook %}Yes{% endif %}",
      "Websites Permitted": "{% if configuration.permittedWebsites %}Yes{% endif %}",
      "Other Permitted Items": "{{configuration.permittedOtherItems | customEscape}}"
    }

    return formattedData;
  }),
  getExamReport: action(function() {
    SessionStore.startLoading('Loading…', 'getExamReport');

    let query = obx.applyFilters();

    obx.reportFormat = obx.formatExamReport;

    // Set up the filter options
    obx.reportFilterMap = {
      institution: 'install',
      examTitle: '',
      courseTitle: '',
      examWindowStart: 'date',
      examWindowEnd: 'date'
    }

    // Store the report URL so we can use it to generate a CSV if we need to
    let reportQuery = obx.updateQueryParam(query, 'limit', '100');

    obx.reportUrl = `/proctors/${SessionStore.userData.proctorAccountSid}/reports/examList${reportQuery}`;

    api.get(`proctors/${SessionStore.userData.proctorAccountSid}/reports/examList${query}`)
      .then(response => {
        obx.reportData = obx.reportFormat(response.data.results);
        obx.reportTotal = response.data.total;

        // Set pagination next url
        if(response.data.next) {
          obx.reportNext = obx.getNextUrl(response.data.next);
        } else {
          obx.reportNext = null;
        }

        SessionStore.finishLoading('getExamReport');
      })
      .catch(error => {
        SessionStore.throwError(error);
      });
  }),
  // Session report
  formatSessionReport: action(function(data) {
    const formattedData = [];

    data.forEach(record => {
      const newRow = {
        student: `${record.enrollment.lastName}, ${record.enrollment.firstName}`,
        date: record.appointmentDate,
        exam: {
          examTitle: record.exam ? record.exam.title : '',
          duration: `Duration: ${formatTime(record.examConfiguration.duration)}`
        },
        status: record.status,
        details: {
          url: `/reports/sessions?session=${record.sid}`,
          title: 'View session',
          class: 'btn--outline',
          action: 'SessionDetail',
          actionType: 'drawer',
          skipCSV: true
        }
      }

      formattedData.push(newRow);
    });

    // Store CSV format
    obx.reportDefinition = {
      "Student": "{{enrollment.lastName | customEscape}}, {{enrollment.firstName | customEscape}}",
      "Date": "{{appointmentDate | time_zone: '"+moment.tz.guess()+"', 'M/D/YYYY [at] h:mma z'}}",
      "Exam": "{{exam.title | customEscape}}",
      "Duration": "{{examConfiguration.duration | customEscape}} minutes",
      "Status": "{{status | customEscape}}"
    }

    return formattedData;
  }),
  getSessionReport: action(function() {
    SessionStore.startLoading('Loading…', 'getSessionReport');

    let query = obx.applyFilters();

    obx.reportFormat = obx.formatSessionReport;

    // Set up the filter options
    obx.reportFilterMap = {
      status: ['Cancelled', 'Closed', 'Scheduled'],
      period: ['upcoming', 'past']
    }

    // Store the report URL so we can use it to generate a CSV if we need to
    let reportQuery = obx.updateQueryParam(query, 'limit', '100');

    obx.reportUrl = `/proctors/${SessionStore.userData.proctorAccountSid}/sessions${reportQuery}`;

    api.get(`proctors/${SessionStore.userData.proctorAccountSid}/sessions${query}`)
      .then(response => {
        obx.reportData = obx.reportFormat(response.data.results);
        obx.reportTotal = response.data.total;

        // Store the sessions in the schedule store
        ScheduleStore.events = response.data.results;

        // Set pagination next url
        if(response.data.next) {
          obx.reportNext = obx.getNextUrl(response.data.next);
        } else {
          obx.reportNext = null;
        }

        SessionStore.finishLoading('getSessionReport');
      })
      .catch(error => {
        SessionStore.throwError(error);
      });
  }),
  // Generate CSV file
  exportData: action(function(callback){
    SessionStore.startLoading('Loading…', 'ExportData');

    let urlBase = 'https://api.dev.smarterproctoring.com/v1';

    if(process.env.REACT_APP_ENVIRONMENT === 'production') {
      urlBase = 'https://api.smarterproctoring.com/v1';
    }

    console.log(SessionStore.token);

    const data = {
      postMetadata: {
        method: obx.reportMethod,
        url: `${urlBase}${obx.reportUrl}`,
        headers: {
          token: SessionStore.token
        }
      },
      exportDocumentProperties: {
        definition: obx.reportDefinition
      },
      title: obx.reportExportTitle
    }

    api.post(`proctors/${SessionStore.userData.proctorAccountSid}/users/${SessionStore.userData.sid}/exports`, data)
      .then(response => {
        if(callback) {
          callback();
        }

        SessionStore.finishLoading('ExportData');
      })
      .catch(error => {
        SessionStore.throwError(error);
      });
  }),
  // Get all generated CSV files
  downloads: [],
  getDownloads: action(function(){
    SessionStore.startLoading('Loading…', 'GetDownloads');

    api.get(`proctors/${SessionStore.userData.proctorAccountSid}/users/${SessionStore.userData.sid}/exports`)
      .then(response => {
        obx.downloads = response.data.results;
        SessionStore.finishLoading('GetDownloads');
      })
      .catch(error => {
        SessionStore.throwError(error);
      });
  }),
});

export default obx;



// WEBPACK FOOTER //
// ./src/app/stores/ReportStore.js